/**
 * Header card d'un OR (extrait de OrDetail.jsx — iter31, refacto iter127 sur DocumentHeader).
 *
 * Utilise DocumentHeader (shared) pour la coquille (icône + numéro + total TTC)
 * et injecte le contenu spécifique OR : badges statut + paiement + archivé,
 * sélecteur type tarif, contact client (tel + email inline), véhicule + km inline.
 */
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Wrench, User, UserCog, Phone, Mail, Archive, FileText, ShieldCheck } from "lucide-react";
import InlineEditField from "@/components/InlineEditField";
import DocumentHeader from "@/components/DocumentHeader";
import api from "@/lib/api";

export default function OrHeader({
  or,
  setOr,
  draft,
  setDraft,
  totaux: t,
  statutLabels,
  statutColors,
  navigate,
  fetchOr,
  onChangeClient,
}) {
  // iter147 — Devis standalone : on n'expose JAMAIS le numero interne 2026-XXXX.
  // Affiche `Devis AN-XXXXX` à la place. Icône FileText (devis) au lieu de Wrench (atelier).
  const isDevisStandalone = or.type_document === "devis";
  const displayNumero = isDevisStandalone
    ? (or.devis_numero || or.numero)
    : or.numero;
  const displayPrefix = isDevisStandalone ? "Devis" : null;
  const headerIcon = isDevisStandalone ? FileText : Wrench;
  const badges = (
    <>
      <Badge variant="outline" className={statutColors[or.statut]}>
        {statutLabels[or.statut]}
      </Badge>
      {or.statut === "pret" && or.facture_id && (
        <Badge
          variant="outline"
          className={
            or.mode_paiement_effectif
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-blue-50 text-blue-700 border-blue-200"
          }
          data-testid="or-paiement-mode"
          title={
            or.mode_paiement_effectif
              ? "Mode de paiement attendu au comptoir"
              : "Facture envoyée, paiement attendu par virement"
          }
        >
          {or.invoice_method
            ? <>📄 Facture émise — {or.invoice_method.charAt(0).toUpperCase() + or.invoice_method.slice(1)}</>
            : or.mode_paiement_effectif
            ? <>💵 Encaissement à livraison ({or.mode_paiement_effectif})</>
            : <>📄 Facture 30 jours</>}
        </Badge>
      )}
      {or.statut === "pret" && !or.facture_id && (
        <Badge
          variant="outline"
          className="bg-orange-50 text-orange-700 border-orange-200"
          data-testid="or-pret-sans-facture"
        >
          ⚠ Facture à émettre (Facturation)
        </Badge>
      )}
      {or.archive && (
        <Badge
          variant="outline"
          className="bg-gray-100 text-gray-600 border-gray-300"
          data-testid="or-archive-badge"
        >
          <Archive className="w-3 h-3 mr-1" /> Archivé
        </Badge>
      )}
      {/* iter233 — Dossier assurance : compagnie + N° de sinistre visibles partout */}
      {or.assurance_detail && (
        <Badge
          variant="outline"
          className="bg-violet-50 text-violet-700 border-violet-200"
          data-testid="or-assurance-badge"
          title="Assurance destinataire des documents"
        >
          <ShieldCheck className="w-3 h-3 mr-1" /> {or.assurance_detail.raison_sociale}
        </Badge>
      )}
      {or.no_sinistre && (
        <Badge
          variant="outline"
          className="bg-violet-50 text-violet-800 border-violet-200 font-mono"
          data-testid="or-sinistre-badge"
        >
          Sinistre {or.no_sinistre}
        </Badge>
      )}
    </>
  );

  const tarifSelect = (
    <Select
      value={draft.type_client_tarif}
      onValueChange={(v) => setDraft((d) => ({ ...d, type_client_tarif: v }))}
    >
      <SelectTrigger className="h-7 w-32 text-xs" data-testid="or-tarif">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="particulier">Particulier</SelectItem>
        <SelectItem value="assurance">Assurance</SelectItem>
        <SelectItem value="flotte">Flotte</SelectItem>
        <SelectItem value="interne">Interne</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <DocumentHeader
      icon={headerIcon}
      numero={displayNumero}
      numeroPrefix={displayPrefix}
      testidNumero={isDevisStandalone ? "devis-numero" : "or-numero"}
      badges={badges}
      extraTopRight={tarifSelect}
      totalTtc={t?.total_ttc}
      monnaie="CHF"
      testidRoot={isDevisStandalone ? "devis-header" : "or-header"}
      bare
    >
      {/* iter252 — mobile : client sur une ligne, contacts en dessous.
          Le numéro est cliquable pour lancer l'appel depuis le téléphone. */}
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2 min-w-0 text-sm">
          <button
            onClick={() => navigate(`/clients/${or.client_id}`)}
            className="text-[#3d8a5c] hover:underline inline-flex items-center gap-1 min-w-0"
          >
            <User className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{or.client_nom}</span>
          </button>
          {onChangeClient && (
            <button
              onClick={onChangeClient}
              title="Changer le client de l'OR"
              aria-label="Changer le client de l'OR"
              className="text-gray-400 hover:text-blue-600 transition-colors shrink-0"
              data-testid="or-change-client-btn"
            >
              <UserCog className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {or.client_detail && (
          <div
            className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-gray-500"
            data-testid="or-client-contact"
          >
            <span className="inline-flex items-center gap-1 min-w-0">
              {or.client_detail.tel_principal ? (
                <a
                  href={`tel:${(or.client_detail.tel_principal || "").replace(/[^+0-9]/g, "")}`}
                  className="text-[#3d8a5c] hover:text-[#2d5f3f] shrink-0"
                  title={`Appeler ${or.client_detail.tel_principal}`}
                  aria-label="Appeler le client"
                  data-testid="or-client-tel-call"
                >
                  <Phone className="w-3.5 h-3.5" />
                </a>
              ) : (
                <Phone className="w-3.5 h-3.5 text-gray-300 shrink-0" />
              )}
              <InlineEditField
                value={or.client_detail.tel_principal}
                onSave={async (v) => {
                  await api.patch(`/clients/${or.client_id}`, { tel_principal: v });
                  await fetchOr();
                }}
                type="tel"
                inputMode="tel"
                placeholder="ajouter tel"
                testId="or-client-tel-inline"
                inputClassName="w-36 text-xs"
              />
            </span>
            <span className="inline-flex items-center gap-1 min-w-0">
              {or.client_detail.email ? (
                <a
                  href={`mailto:${or.client_detail.email}`}
                  className="text-[#3d8a5c] hover:text-[#2d5f3f] shrink-0"
                  title={`Écrire à ${or.client_detail.email}`}
                  aria-label="Écrire au client"
                  data-testid="or-client-email-link"
                >
                  <Mail className="w-3.5 h-3.5" />
                </a>
              ) : (
                <Mail className="w-3.5 h-3.5 text-gray-300 shrink-0" />
              )}
              <InlineEditField
                value={or.client_detail.email}
                onSave={async (v) => {
                  await api.patch(`/clients/${or.client_id}`, { email: v });
                  await fetchOr();
                }}
                type="email"
                inputMode="email"
                placeholder="ajouter email"
                testId="or-client-email-inline"
                inputClassName="w-40 sm:w-48 text-xs"
              />
            </span>
          </div>
        )}
      </div>
    </DocumentHeader>
  );
}
